'use client'

import { WagmiAdapter } from '@reown/appkit-adapter-wagmi'
import { type Chain, custom, http, RpcRequestError, type Transport } from 'viem'
import { getHttpRpcClient } from 'viem/utils'
import {
  type Config,
  cookieStorage,
  createConfig,
  type CreateConfigParameters,
  type CreateConnectorFn,
  createStorage,
  fallback
} from 'wagmi'
import { baseAccount, mock } from 'wagmi/connectors'

import type { ChainId } from '../web3.types'
import type { Chain as AppChain } from './chains/chain.config'

import { getChain, getChains } from '../utils/chain.utils'
import { getAnvilRpcUrl } from './anvil'
import { limitBatchCalldataBytes } from './batching'
import { rpcs } from './rpcs'

type WagmiRpcOptions = {
  include?: Partial<{
    secret: boolean
    proxied: boolean
    public: boolean
  }>
  /**
   * Reads `ANVIL_RPC_URLS` / `ERPC_SECRET`. Only consulted when `include.secret`
   * is set; optional otherwise, so consumers that bring their own `transports`
   * (and never resolve secret RPC URLs) don't have to pass a no-op.
   */
  getEnvVar?: (name: 'ANVIL_RPC_URLS' | 'ERPC_SECRET') => string | undefined
}

function getRpcUrlsForChain(chain: Chain, opts: WagmiRpcOptions): string[] {
  const include = opts.include ?? {}
  const chainId = chain.id as ChainId
  const urls: (string | undefined)[] = []

  if (include.secret) {
    if (!opts.getEnvVar) {
      throw new Error('getWagmiAdapter: `getEnvVar` is required when `include.secret` is set')
    }
    urls.push(getAnvilRpcUrl(chainId, opts.getEnvVar))
    urls.push(
      rpcs.erpc.chain(chainId).with({ secret: opts.getEnvVar('ERPC_SECRET'), endpoint: 'cache' })
        .url
    )
  }
  if (include.proxied) urls.push(`/api/rpc/${chainId}`)
  if (include.public) urls.push(...chain.rpcUrls.default.http)

  const filtered = urls.filter((u): u is string => !!u)
  if (filtered.length === 0) {
    console.warn(`No RPC URLs found for ${chain.name}. Check \`include\` args and env vars.`)
  }
  return filtered
}

const defaultConnectors = [
  // Pre-configured to disable Base Account's telemetry. AppKit auto-injects
  // baseAccount() (telemetry on by default) unless a connector with id
  // 'baseAccount' already exists — so we register ours first.
  baseAccount({ preference: { options: 'all', telemetry: false } })
  // Intentionally NOT registering coinbaseWallet(): its 'com.coinbase.wallet'
  // rdns makes wagmi dedupe (hide) the Coinbase Prime extension, which announces
  // the same rdns via EIP-6963. See CRTR-2603.
]

/**
 * RPC deadline for every E2E request, wallet or read. Anvil answers a deploy
 * transaction only after pulling every cold storage slot it touches from the
 * fork upstream, which routinely runs past viem's 10s HTTP default; a real
 * wallet imposes no such deadline, so E2E waits comfortably longer.
 */
const E2E_RPC_TIMEOUT = 120_000

/**
 * wagmi's `mock` connector forwards `eth_sendTransaction` through viem's default
 * 10s HTTP timeout with `retryCount: 0`, so one slow anvil response aborts the
 * transaction outright. This re-issues that single method on a client with
 * {@link E2E_RPC_TIMEOUT} and delegates everything else untouched.
 */
function e2eMock(parameters: Parameters<typeof mock>[0]): CreateConnectorFn {
  const create = mock(parameters)
  return config => {
    const connector = create(config)
    return {
      ...connector,
      async getProvider(params?: { chainId?: number }) {
        const provider = await connector.getProvider(params)
        const chain = config.chains.find(c => c.id === params?.chainId) ?? config.chains[0]
        const [url] = chain.rpcUrls.default.http
        if (url === undefined) throw new Error(`No default RPC URL for chain ${chain.id}`)
        const client = getHttpRpcClient(url, { timeout: E2E_RPC_TIMEOUT })

        return custom({
          async request({ method, params: args }) {
            if (method !== 'eth_sendTransaction') return provider.request({ method, params: args })

            const body = { method, params: args }
            const { error, result } = await client.request({ body })
            if (error) throw new RpcRequestError({ body, error, url })
            return result
          }
        })({ retryCount: 0 })
      }
    }
  }
}

const e2eConnectors = [
  e2eMock({
    accounts: ['0xf39Fd6e51aad88F6F4ce6aB8827279cffFb92266'], // Default anvil account
    features: { defaultConnected: true }
  })
]

function getRpcUrlsForChains(chainIds: readonly number[], opts: WagmiRpcOptions) {
  return Object.fromEntries(
    chainIds.map(chainId => {
      return [chainId, getRpcUrlsForChain(getChain(chainId), opts)]
    })
  ) as Record<(typeof chainIds)[number], string[]>
}

type GetWagmiAdapterReturnType = {
  wagmiConfig: Config
  wagmiAdapter: WagmiAdapter
}

export function getWagmiAdapter({
  chainIds,
  projectId,
  transports,
  chains,
  connectors,
  batch,
  options
}: {
  chainIds: readonly ChainId[]
  projectId: string
  /**
   * Prebuilt transports per chain, keyed by chain id. Accepts any wagmi
   * `Transport` (a plain `http`, a `fallback([...])`, an instrumented proxy
   * transport, …) — a consumer that already builds its own transports doesn't
   * have to wrap each one in `fallback()` just to satisfy the type. When
   * omitted, the RPC-url map builds a `fallback()` transport per chain.
   */
  transports?: Record<number, Transport>
  chains?: [AppChain, ...AppChain[]]
  connectors?: CreateConnectorFn[]
  batch?: CreateConfigParameters['batch']
  options: WagmiRpcOptions & {
    ssr?: boolean
    isE2E?: boolean
    useCookieStorage?: boolean
    /**
     * Explicit wagmi storage. Takes precedence over `useCookieStorage` — e.g. a
     * consumer supplies `localStorage` for Safe-app iframes, where third-party
     * cookies are blocked. When omitted, storage falls back to
     * `useCookieStorage` (cookie storage by default).
     */
    storage?: CreateConfigParameters['storage']
  }
}): GetWagmiAdapterReturnType {
  // Set default options where necessary
  options.ssr ??= true
  options.useCookieStorage ??= true
  // Set default options.include where necessary (tailored for client)
  const include = { ...options?.include }
  include.secret ??= false
  include.proxied ??= true
  include.public ??= !options.isE2E
  // Constrain multicall.batchSize in E2E mode (to avoid overwhelming anvil)
  if (options.isE2E) {
    batch = limitBatchCalldataBytes(batch, 2 ** 15)
  }

  const rpcUrlsMap = getRpcUrlsForChains(chainIds, { ...options, include })

  let _chains = chains ?? getChains(chainIds)
  // NOTE: Mock connector uses default http URL to call `eth_sendTransaction`, so
  // we must redirect it to anvil in E2E mode
  if (options.isE2E) {
    _chains = _chains.map(chain => ({
      ...chain,
      rpcUrls: { default: { http: rpcUrlsMap[chain.id as number] } }
    })) as unknown as typeof _chains
  }

  // If no transports are passed in, create a fallback transport for all
  // RPC URLs with default retry counts/delays.
  // TODO: many of these `http` transports could take advantage of RPC-level
  // batching (independent of multicall)
  // Reads share the wallet's problem: anvil can sit on a request for far longer
  // than viem's 10s default, and an abort here just burns a retry cycle.
  const timeout = options.isE2E ? E2E_RPC_TIMEOUT : undefined
  const _transports =
    transports ??
    Object.fromEntries(
      Object.entries(rpcUrlsMap).map(([chainId, rpcUrls]) => {
        return [chainId, fallback(rpcUrls.map(rpcUrl => http(rpcUrl, { timeout })))]
      })
    )

  // If no connectors are passed in, use either:
  // (a) A mock connector (if E2E)
  // (b) A default connector
  // AppKit will inject browser wallets at runtime
  const _connectors = options.isE2E ? e2eConnectors : (connectors ?? defaultConnectors)

  // An explicit `storage` option wins (e.g. localStorage for Safe-app iframes).
  // Otherwise default to cookie storage, only disabled if explicitly requested.
  const storage =
    options.storage ??
    (options.useCookieStorage ? createStorage({ storage: cookieStorage }) : undefined)

  const wagmiAdapter = new WagmiAdapter({
    storage,
    ssr: options.ssr,
    projectId,
    networks: _chains,
    transports: _transports,
    connectors: _connectors,
    batch
  })

  // NOTE: AppKit inevitably inserts a WalletConnect RPC, so for E2E tests
  // we override with a plain wagmi config
  const wagmiConfig = options.isE2E
    ? createConfig({
        storage,
        ssr: options.ssr,
        chains: _chains as [Chain, ...Chain[]],
        transports: _transports,
        connectors: _connectors,
        batch
      })
    : wagmiAdapter.wagmiConfig

  return { wagmiConfig, wagmiAdapter }
}
